import { z } from "zod";
import {
  InvalidBlockNoteDocumentError,
  parseNonEmptyBlockNoteDocument,
  stringifyBlockNoteDocumentForStorage,
} from "../../lib/blockNoteDocument";
import {
  richTextFromPlainText,
  richTextToPlainText,
} from "../../lib/tableRichTextCell";
import {
  TEXT_AGGREGATES,
  compareText,
  fail,
  ok,
  textOperators,
  type FieldKind,
} from "../kind";
import { scalarToText } from "./text";

/**
 * Un document BlockNote sérialisé en JSON : la convention de tous les
 * documents de l'app (`nodeDatas.values.doc`). Sérialisé parce qu'un
 * document dépasse vite les limites de Convex sur la profondeur et les clés
 * des objets stockés.
 */
export type RichTextValue = string;

export const richTextSettings = z.object({
  // Faux : texte long brut, sans gras, titres ni listes.
  formatting: z.boolean().default(true),
  placeholder: z.string().max(200).optional(),
  // Hauteur d'une cellule dans la grille, en lignes.
  maxCellLines: z.number().int().min(1).max(10).default(3),
});

export const MAX_RICH_TEXT_BYTES = 200_000;

function plainText(value: RichTextValue): string {
  return richTextToPlainText(value);
}

export const richTextKind: FieldKind<"rich_text"> = {
  type: "rich_text",
  settings: richTextSettings,
  value: z.string().min(2).max(MAX_RICH_TEXT_BYTES),
  coerce(input, settings) {
    // Un document : tableau de blocs, ou sa forme sérialisée.
    const doc = Array.isArray(input)
      ? input
      : typeof input === "string" && input.trimStart().startsWith("[")
        ? parseNonEmptyBlockNoteDocument(input)
        : null;

    let stored: string;
    if (doc) {
      if (doc.length === 0) return ok(null);
      try {
        stored = stringifyBlockNoteDocumentForStorage(doc);
      } catch (error) {
        if (error instanceof InvalidBlockNoteDocumentError) {
          return fail(`Invalid document: ${error.message}`);
        }
        throw error;
      }
      if (!settings.formatting) stored = richTextFromPlainText(plainText(stored));
    } else {
      const text = scalarToText(input);
      if (text === null) return fail("Expected text or a BlockNote document.");
      if (text.trim() === "") return ok(null);
      stored = richTextFromPlainText(text);
    }
    if (plainText(stored).trim() === "") return ok(null);
    if (stored.length > MAX_RICH_TEXT_BYTES) return fail("Document is too large.");
    return ok(stored);
  },
  isEmpty: (value) => plainText(value).trim() === "",
  format: (value) => plainText(value),
  compare: (a, b) => compareText(plainText(a), plainText(b)),
  filters: textOperators<"rich_text">(plainText).filter(
    (operator) => operator.id === "contains" || operator.id === "not_contains",
  ),
  aggregates: TEXT_AGGREGATES.filter((id) => id !== "count_unique"),
};
